import { combineReducers } from 'redux';

import * as fromAgents from './agents';
import flights, * as fromFlights from './flights';
import places from './places';
import * as fromPlaces from './places/selectors';

import type { FlightState } from './flights';
import type { Alliance } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/alliances';
import type {
  Carrier,
  CarrierMultipleAggregate,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/carriers';
import type {
  Itinerary,
  ItineraryById,
  FilterStatusMap,
} from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries';
import type { Leg } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/legs';
import type { Segment } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/segments';
import type {
  Place,
  PlaceState,
} from '@web-platform/shared-universal-types/src/redux/state/places/places';
import type {
  Agent,
  AgentState,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightSearchResults/agents';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

export type EntitiesState = {
  agents: AgentState;
  flights: FlightState;
  places: PlaceState;
};

export default combineReducers({
  agents: fromAgents.agentsReducer,
  flights,
  places,
});

export const flightLeg = (state: EntitiesState, id: string): Leg | undefined =>
  fromFlights.leg(state.flights, id);
export const flightAllLegs = (state: EntitiesState): Record<string, Leg> =>
  fromFlights.allLegs(state.flights);
export const flightSegment = (state: EntitiesState, id: string): Segment | undefined =>
  fromFlights.segment(state.flights, id);
export const flightAllSegments = (state: EntitiesState): Record<string, Segment> =>
  fromFlights.allSegments(state.flights);
export const flightAlliance = (state: EntitiesState, id: number): Alliance | undefined =>
  fromFlights.alliance(state.flights, id);
export const flightAllAlliances = (state: EntitiesState): Record<number, Alliance> =>
  fromFlights.allAlliances(state.flights);
export const flightCarrier = (state: EntitiesState, id: number): Carrier | undefined =>
  fromFlights.carrier(state.flights, id);
export const flightAllCarriers = (state: EntitiesState): Record<number, Carrier> =>
  fromFlights.allCarriers(state.flights);
export const flightCarriersMultiple = (state: EntitiesState): CarrierMultipleAggregate | null =>
  fromFlights.carriersMultiple(state.flights);
export const flightCarriersCurrentResultIds = (state: EntitiesState): number[] =>
  fromFlights.carriersCurrentResultIds(state.flights);
export const flightItinerary = (state: EntitiesState, id: string): Itinerary =>
  fromFlights.itinerary(state.flights, id);
export const flightAllItineraries = (state: EntitiesState): ItineraryById =>
  fromFlights.allItineraries(state.flights);
export const getItineraryFilterStatusMap = (state: EntitiesState, id: string): FilterStatusMap[] =>
  fromFlights.getItineraryFilterStatusMap(state.flights, id);

// entities - agents
export const agent = (state: EntitiesState, id: string): Agent | undefined =>
  fromAgents.get(state.agents, id);
export const allAgents = (state: EntitiesState): Record<string, Agent> =>
  fromAgents.getAll(state.agents);

// entities - places
export const flightPlaceByEntityId = (state: EntitiesState, entityId: EntityId): Place =>
  fromPlaces.getByEntityId(state.places, entityId);

export const flightAllPlacesByEntityId = (state: EntitiesState): Record<EntityId, Place> =>
  fromPlaces.allByEntityId(state.places);
